import { PortableTextMarkComponentProps } from '@portabletext/react'
import { useState } from 'react'
import { FiChevronDown } from 'react-icons/fi'

import { InternalLink } from 'src/components/atoms/Link'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { FaqSectionRebrandResult } from 'src/components/layouts/Page/SanityContentPage/types'
import { Text } from 'src/components/molecules/sanity/Content'

import { FaqItem } from 'src/config/generated/sanitySchema'

// Render answer links in forestDeep (default Sanity link mark uses `primary`).
const AnswerLink = ({
  children,
  value
}: PortableTextMarkComponentProps<{ _type: 'link'; href: string }>) => (
  <InternalLink
    color='none'
    href={value?.href || ''}
    className='text-forestDeep underline underline-offset-2 hover:opacity-80'
  >
    {children}
  </InternalLink>
)

const BACKGROUND_CLASS: Record<
  NonNullable<FaqSectionRebrandResult['backgroundColor']>,
  string
> = {
  limeFresh: 'bg-limeFresh/50',
  linen: 'bg-linen'
}

const FaqItemRow = ({ question, answer }: FaqItem) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <li className='border-b border-espresso/20'>
      <button
        type='button'
        aria-expanded={isOpen}
        onClick={() => setIsOpen(prev => !prev)}
        className='flex w-full items-center justify-between gap-4 py-6 text-left'
      >
        <TypographyRebrand variant='h5' color='espresso'>
          {question}
        </TypographyRebrand>
        <FiChevronDown
          aria-hidden
          className={`shrink-0 text-espresso transition-transform duration-300 ${
            isOpen ? '-rotate-180' : ''
          }`}
          size={32}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className='overflow-hidden'>
          <div className='pb-6'>
            <TypographyRebrand variant='body' color='espresso'>
              <Text
                components={{
                  block: ({ children }) => (
                    <p className='mb-4 last:mb-0'>{children}</p>
                  ),
                  marks: { link: AnswerLink }
                }}
                value={answer}
              />
            </TypographyRebrand>
          </div>
        </div>
      </div>
    </li>
  )
}

export const FaqSectionRebrand = ({
  eyebrow,
  heading,
  intro,
  faqItems,
  backgroundColor
}: FaqSectionRebrandResult) => {
  return (
    <section
      className={`w-full ${BACKGROUND_CLASS[backgroundColor ?? 'limeFresh']}`}
    >
      <div className='container mx-auto px-4 py-12 lg:px-20 lg:py-18'>
        {eyebrow && (
          <TypographyRebrand variant='eyebrow' color='espresso'>
            {eyebrow}
          </TypographyRebrand>
        )}
        <div className='mb-4'>
          <TypographyRebrand variant='h2' color='espresso'>
            <strong>{heading}</strong>
          </TypographyRebrand>
        </div>
        {intro && (
          <TypographyRebrand variant='introduction' color='espresso'>
            {intro}
          </TypographyRebrand>
        )}
        {faqItems && faqItems.length > 0 && (
          <ul className='mt-2 flex flex-col border-t border-espresso/20'>
            {faqItems.map(item => (
              <FaqItemRow key={item._key} {...item} />
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

export default FaqSectionRebrand
